import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useBrand } from '../context/BrandContext';
import { resolveActiveBrandChrome } from '../activeBrandChrome';
import {
  isBrandManagerUserManagementPath,
  isBrandShellUserManagementPath,
} from '../brandManagerPaths';
import apiService from '../services/api';
import { resolveBrandUserDisplayName } from '../brandUserIdentity';
import '../brandManagerPages.css';

/** Brand-manager APIs wrap payloads as `{ success, data }`. */
function unwrapApiData(payload) {
  return payload?.data ?? payload;
}

function mapBrandUserFromApi(apiUser) {
  const auth0Sub = String(apiUser?.auth0Sub || '').trim();
  const email = String(apiUser?.email || '').trim();
  const displayName = String(apiUser?.displayName || '').trim();
  return {
    id: auth0Sub || (email ? `email:${email}` : null),
    auth0Sub: auth0Sub || null,
    name: resolveBrandUserDisplayName({ displayName, email, auth0Sub }),
    email: email || '—',
    role: apiUser?.brandRole || '—',
    accessLevel: apiUser?.accessLevel || '—',
    roleScope: apiUser?.roleScope || null,
    status: apiUser?.status || '—',
    assignedLocationCount: apiUser?.assignedLocationCount,
    lastLoginAt: apiUser?.lastLoginAt || null,
  };
}

function formatBrandLastLogin(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function brandUserLocationsLabel(user) {
  const access = String(user.accessLevel || '').trim();
  if (access === 'Full' || user.roleScope === 'corporate') {
    return 'All';
  }
  if (user.assignedLocationCount != null && user.assignedLocationCount !== '') {
    return String(user.assignedLocationCount);
  }
  return '—';
}

/** Canonical Client / Master Account roles (UserMembership.role). No Editor/Uploader. */
const CANONICAL_CLIENT_ROLES = [
  {
    id: 'owner',
    name: 'owner',
    label: 'Owner',
    description: 'Full Client administration for this Master Account',
  },
  {
    id: 'admin',
    name: 'admin',
    label: 'Admin',
    description: 'Administer users and Client settings',
  },
  {
    id: 'manager',
    name: 'manager',
    label: 'Manager',
    description: 'Manage day-to-day Client operations',
  },
  {
    id: 'viewer',
    name: 'viewer',
    label: 'Viewer',
    description: 'View-only Client access',
  },
];

/** Brand Role WHAT values (UserBrandAccess.accessLevel). Orthogonal to Full/Limited WHERE. */
const CANONICAL_BRAND_ROLES = [
  {
    id: 'admin',
    name: 'admin',
    label: 'admin',
    description: 'Administer content and settings for the Brand (WHAT)',
  },
  {
    id: 'manage',
    name: 'manage',
    label: 'manage',
    description: 'Manage Brand content (WHAT)',
  },
  {
    id: 'view',
    name: 'view',
    label: 'view',
    description: 'View-only Brand capabilities (WHAT)',
  },
];

function mapPortfolioUserFromApi(apiUser) {
  const auth0Sub = String(apiUser?.auth0Sub || '').trim();
  const email = String(apiUser?.email || '').trim();
  const displayName = String(apiUser?.displayName || '').trim();
  const brands = Array.isArray(apiUser?.brands) ? apiUser.brands : [];
  return {
    id: auth0Sub || (email ? `email:${email}` : null),
    auth0Sub: auth0Sub || null,
    name: resolveBrandUserDisplayName({ displayName, email, auth0Sub }),
    email: email || '—',
    role: apiUser?.role || '—',
    status: apiUser?.status || '—',
    lastLoginAt: apiUser?.lastLoginAt || null,
    brands,
  };
}

function formatPortfolioBrands(user) {
  const brands = Array.isArray(user.brands) ? user.brands : [];
  if (!brands.length) return '—';
  return brands
    .map((b) => {
      const name = b.brandName || b.brandId || 'Brand';
      const where = b.accessLevel ? ` (${b.accessLevel})` : '';
      return `${name}${where}`;
    })
    .join(', ');
}

const UserManagementPage = ({ activeTab: initialActiveTab }) => {
  const { userInfo } = useUser();
  const location = useLocation();
  const { currentBrand } = useBrand();
  const activeBrandChrome = resolveActiveBrandChrome(currentBrand);
  const isBrandScoped = isBrandShellUserManagementPath(location.pathname);
  const isPortfolioScoped = isBrandManagerUserManagementPath(location.pathname);
  
  // User roster is loaded from the API when available. Never seed demo people.
  const [users, setUsers] = useState([]);
  const [brandUsersLoading, setBrandUsersLoading] = useState(false);
  const [brandUsersError, setBrandUsersError] = useState(null);
  const brandId = isBrandScoped ? activeBrandChrome?.id : null;
  
  // Roles are derived from canonical enums + live roster counts — never invented.
  const [roles, setRoles] = useState([]);
  
  // Modal states
  const [activeRoleMenu, setActiveRoleMenu] = useState(null);
  const [activeUserMenu, setActiveUserMenu] = useState(null);
  const actionsRef = useRef(null);
  
  // Add these state variables for modals
  const [showNewUserModal, setShowNewUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [showNewRoleModal, setShowNewRoleModal] = useState(false);
  const [showEditRoleModal, setShowEditRoleModal] = useState(false);
  const [showDeleteRoleModal, setShowDeleteRoleModal] = useState(false);
  const [showAssignRoleModal, setShowAssignRoleModal] = useState(false);

  // Add state for selected items
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);

  // Add state for form data
  const [newUser, setNewUser] = useState({ name: '', email: '', role: '', authentication: 'Two-step' });
  const [newRole, setNewRole] = useState({ name: '', description: '', permissions: 'None' });
  const [searchTerm, setSearchTerm] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  
  // Get initial tab from URL or prop
  const getInitialTab = () => {
    if (location.pathname.includes('/roles')) {
      return 'roles';
    } else if (initialActiveTab) {
      return initialActiveTab;
    } else {
      return 'users';
    }
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab());
  
  // Close dropdown menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (actionsRef.current && !actionsRef.current.contains(event.target)) {
        setActiveRoleMenu(null);
        setActiveUserMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (isBrandScoped) {
      if (!brandId) {
        setUsers([]);
        setBrandUsersLoading(false);
        setBrandUsersError(null);
        return undefined;
      }

      setBrandUsersLoading(true);
      setBrandUsersError(null);

      (async () => {
        try {
          const res = await apiService.listBrandUsers(brandId);
          if (cancelled) return;
          const payload = unwrapApiData(res);
          const list = Array.isArray(payload?.users) ? payload.users : [];
          setUsers(list.map(mapBrandUserFromApi).filter((u) => u.id));
        } catch (err) {
          if (cancelled) return;
          setUsers([]);
          setBrandUsersError(
            err?.message || 'Failed to load users for this brand.',
          );
        } finally {
          if (!cancelled) setBrandUsersLoading(false);
        }
      })();

      return () => {
        cancelled = true;
      };
    }

    if (isPortfolioScoped) {
      setBrandUsersLoading(true);
      setBrandUsersError(null);

      (async () => {
        try {
          const res = await apiService.listPortfolioUsers();
          if (cancelled) return;
          const payload = unwrapApiData(res);
          const list = Array.isArray(payload?.users) ? payload.users : [];
          setUsers(list.map(mapPortfolioUserFromApi).filter((u) => u.id));
        } catch (err) {
          if (cancelled) return;
          setUsers([]);
          setBrandUsersError(
            err?.message || 'Failed to load portfolio users.',
          );
        } finally {
          if (!cancelled) setBrandUsersLoading(false);
        }
      })();

      return () => {
        cancelled = true;
      };
    }

    setUsers([]);
    setBrandUsersLoading(false);
    setBrandUsersError(null);
    return undefined;
  }, [isBrandScoped, isPortfolioScoped, brandId]);

  useEffect(() => {
    if (isPortfolioScoped) {
      const counts = Object.fromEntries(
        CANONICAL_CLIENT_ROLES.map((r) => [r.name, 0]),
      );
      users.forEach((u) => {
        const key = String(u.role || '').trim();
        if (Object.prototype.hasOwnProperty.call(counts, key)) {
          counts[key] += 1;
        }
      });
      setRoles(
        CANONICAL_CLIENT_ROLES.map((r) => ({
          ...r,
          permissions: 'Client role (WHAT)',
          users: counts[r.name],
        })),
      );
      return;
    }
    if (isBrandScoped) {
      const counts = Object.fromEntries(
        CANONICAL_BRAND_ROLES.map((r) => [r.name, 0]),
      );
      users.forEach((u) => {
        const key = String(u.role || '').trim();
        if (Object.prototype.hasOwnProperty.call(counts, key)) {
          counts[key] += 1;
        }
      });
      setRoles(
        CANONICAL_BRAND_ROLES.map((r) => ({
          ...r,
          permissions: 'Brand role (WHAT)',
          users: counts[r.name],
        })),
      );
    }
  }, [users, isPortfolioScoped, isBrandScoped]);
  
  // Helper function to update role user counts
  const updateRoleUserCounts = () => {
    return roles;
  };
  
  // Add handlers for User actions
  const handleAddUserClick = () => {
    setShowNewUserModal(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowEditUserModal(true);
    setActiveUserMenu(null);
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setShowDeleteUserModal(true);
    setActiveUserMenu(null);
  };

  const handleCreateUser = () => {
    if (newUser.name && newUser.email && newUser.role) {
      const userToAdd = {
        id: users.length + 1,
        name: newUser.name,
        email: newUser.email,
        role: newUser.role,
        authentication: newUser.authentication,
        lastLogin: 'Never'
      };
      
      setUsers([...users, userToAdd]);
      setShowNewUserModal(false);
      setNewUser({ name: '', email: '', role: '', authentication: 'Two-step' });
    } else {
      alert('Please fill out all required fields');
    }
  };

  const handleSaveUserEdit = () => {
    if (selectedUser) {
      // Update the user
      setUsers(users.map(user => 
        user.id === selectedUser.id 
          ? { ...user, ...selectedUser } 
          : user
      ));
      
      // Update role user counts
      const updatedRoleCounts = {};
      roles.forEach(role => {
        updatedRoleCounts[role.name] = 0;
      });
      
      const updatedUsers = users.map(user => 
        user.id === selectedUser.id ? { ...user, ...selectedUser } : user
      );
      
      updatedUsers.forEach(user => {
        if (user.role && updatedRoleCounts.hasOwnProperty(user.role)) {
          updatedRoleCounts[user.role]++;
        }
      });
      
      // Update the roles with new counts
      setRoles(roles.map(role => ({
        ...role,
        users: updatedRoleCounts[role.name] || 0
      })));
      
      setShowEditUserModal(false);
      setSelectedUser(null);
    }
  };

  const handleConfirmDeleteUser = () => {
    if (selectedUser) {
      setUsers(users.filter(user => user.id !== selectedUser.id));
      setShowDeleteUserModal(false);
      setSelectedUser(null);
    }
  };

  // Add handlers for Role actions
  const handleCreateRoleClick = () => {
    setShowNewRoleModal(true);
  };

  const handleEditRole = (role) => {
    setSelectedRole(role);
    setShowEditRoleModal(true);
    setActiveRoleMenu(null);
  };

  const handleDeleteRole = (role) => {
    setSelectedRole(role);
    setShowDeleteRoleModal(true);
    setActiveRoleMenu(null);
  };

  const handleAssignToUsers = (role) => {
    setSelectedRole(role);
    setShowAssignRoleModal(true);
    setActiveRoleMenu(null);
  };

  const handleCreateRole = () => {
    if (newRole.name && newRole.description) {
      const roleToAdd = {
        id: roles.length + 1,
        name: newRole.name,
        description: newRole.description,
        permissions: newRole.permissions,
        users: 0
      };
      
      setRoles([...roles, roleToAdd]);
      setShowNewRoleModal(false);
      setNewRole({ name: '', description: '', permissions: 'None' });
    } else {
      alert('Please fill out all required fields');
    }
  };

  const handleSaveRoleEdit = () => {
    if (selectedRole) {
      setRoles(roles.map(role => 
        role.id === selectedRole.id 
          ? { ...role, ...selectedRole } 
          : role
      ));
      setShowEditRoleModal(false);
      setSelectedRole(null);
    }
  };

  const handleConfirmDeleteRole = () => {
    if (selectedRole) {
      setRoles(roles.filter(role => role.id !== selectedRole.id));
      setShowDeleteRoleModal(false);
      setSelectedRole(null);
    }
  };

  const handleUserSearch = (e) => {
    setSearchTerm(e.target.value);
    setShowUserDropdown(true);
  };

  const handleAssignRole = () => {
    if (selectedUser && selectedRole) {
      // Create a new array with the updated user
      const updatedUsers = users.map(user => {
        if (user.id === selectedUser.id) {
          return { ...user, role: selectedRole.name };
        }
        return user;
      });
      
      // Update users state
      setUsers(updatedUsers);
      
      // Calculate role counts with the updated users
      const roleCounts = {};
      roles.forEach(role => {
        roleCounts[role.name] = 0;
      });
      
      updatedUsers.forEach(user => {
        if (user.role && roleCounts.hasOwnProperty(user.role)) {
          roleCounts[user.role]++;
        }
      });
      
      // Update roles with new counts
      const updatedRoles = roles.map(role => ({
        ...role,
        users: roleCounts[role.name] || 0
      }));
      
      setRoles(updatedRoles);
      
      // Close modal and reset state
      setShowAssignRoleModal(false);
      setSearchTerm('');
      setSelectedUser(null);
    }
  };
  
  const renderBrandScopedUsersTab = () => (
    <>
      <div className="profile-header" style={{
        marginBottom: '30px',
        width: '100%',
      }}>
        <h2>Users</h2>
      </div>

      {brandUsersLoading ? (
        <p data-testid="brand-users-loading">Loading brand users…</p>
      ) : null}
      {brandUsersError ? (
        <p
          data-testid="brand-users-error"
          style={{ color: '#E32726', marginBottom: '16px' }}
          role="alert"
        >
          {brandUsersError}
        </p>
      ) : null}
      {!brandId && !brandUsersLoading ? (
        <p data-testid="brand-users-no-brand">Select a brand to view users.</p>
      ) : null}

      {!brandUsersLoading && !brandUsersError && brandId && users.length === 0 ? (
        <p data-testid="brand-users-empty">No users with access to this brand yet.</p>
      ) : null}

      {users.length > 0 ? (
      <div
        className="users-table brand-users-table"
        data-testid="brand-users-table"
        style={{ marginTop: '20px', backgroundColor: 'white', borderRadius: '8px' }}
      >
        <div
          className="table-header"
          style={{
            display: 'grid',
            gridTemplateColumns: '1.4fr 1.8fr 0.9fr 0.9fr 0.8fr 0.8fr 1.1fr',
            padding: '12px 16px',
            backgroundColor: '#1C2632',
            color: 'white',
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px',
            columnGap: '12px',
          }}
        >
          <div className="col-user" style={{ color: 'white' }}>User</div>
          <div className="col-email" style={{ color: 'white' }}>Email</div>
          <div className="col-role" style={{ color: 'white' }}>Role</div>
          <div className="col-access" style={{ color: 'white' }}>Access</div>
          <div className="col-status" style={{ color: 'white' }}>Status</div>
          <div className="col-locations" style={{ color: 'white' }}>Locations</div>
          <div className="col-login" style={{ color: 'white' }}>Latest Login</div>
        </div>

        {users.map((user) => (
          <div
            className="table-row"
            key={user.id}
            data-testid="brand-user-row"
            style={{
              display: 'grid',
              gridTemplateColumns: '1.4fr 1.8fr 0.9fr 0.9fr 0.8fr 0.8fr 1.1fr',
              padding: '12px 16px',
              borderBottom: '1px solid #e5e5e5',
              alignItems: 'center',
              columnGap: '12px',
            }}
          >
            <div className="col-user" style={{ fontWeight: 500, minWidth: 0 }}>
              {user.name}
            </div>
            <div
              className="col-email"
              data-testid="brand-user-email"
              style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {user.email && user.email !== '—' ? user.email : '—'}
            </div>
            <div className="col-role">{user.role}</div>
            <div className="col-access">{user.accessLevel}</div>
            <div className="col-status">{user.status}</div>
            <div className="col-locations">{brandUserLocationsLabel(user)}</div>
            <div className="col-login" style={{ whiteSpace: 'nowrap' }}>
              {formatBrandLastLogin(user.lastLoginAt)}
            </div>
          </div>
        ))}
      </div>
      ) : null}
    </>
  );

  // Render the Users tab content
  const renderPortfolioUsersTab = () => (
    <>
      <div className="profile-header" style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '30px',
        position: 'relative',
        width: '100%'
      }}>
        <h2>Users</h2>
      </div>

      {brandUsersLoading ? (
        <p data-testid="portfolio-users-loading">Loading portfolio users…</p>
      ) : null}
      {brandUsersError ? (
        <p data-testid="portfolio-users-error" style={{ color: '#a11a20' }}>
          {brandUsersError}
        </p>
      ) : null}
      
      <div
        className="users-table"
        style={{ marginTop: '20px', backgroundColor: 'white', borderRadius: '8px' }}
        data-testid="portfolio-users-table"
      >
        <div className="table-header" style={{ display: 'grid', gridTemplateColumns: '1.4fr 1.8fr 1.6fr 0.9fr 0.8fr 1.1fr', padding: '12px 16px', backgroundColor: '#1C2632', color: 'white', borderTopLeftRadius: '8px', borderTopRightRadius: '8px' }}>
          <div className="col-name" style={{ color: 'white' }}>Name</div>
          <div className="col-email" style={{ color: 'white' }}>Email</div>
          <div className="col-brands" style={{ color: 'white' }}>Brands</div>
          <div className="col-role" style={{ color: 'white' }}>Role</div>
          <div className="col-status" style={{ color: 'white' }}>Status</div>
          <div className="col-login" style={{ color: 'white' }}>Latest Login</div>
        </div>

        {!brandUsersLoading && !brandUsersError && users.length === 0 ? (
          <div
            style={{ padding: '24px 16px', color: '#777' }}
            data-testid="portfolio-users-empty"
          >
            No Client users found for this Master Account.
          </div>
        ) : null}
        
        {users.map((user) => (
          <div
            className="table-row"
            key={user.id}
            data-testid="portfolio-user-row"
            style={{ display: 'grid', gridTemplateColumns: '1.4fr 1.8fr 1.6fr 0.9fr 0.8fr 1.1fr', padding: '12px 16px', borderBottom: '1px solid #e5e5e5', alignItems: 'center', columnGap: '12px' }}
          >
            <div className="col-name" style={{ fontWeight: 500 }}>{user.name}</div>
            <div className="col-email">{user.email}</div>
            <div className="col-brands" data-testid="portfolio-user-brands">
              {formatPortfolioBrands(user)}
            </div>
            <div className="col-role" data-testid="portfolio-user-role">{user.role}</div>
            <div className="col-status">{user.status}</div>
            <div className="col-login" style={{ whiteSpace: 'nowrap' }}>
              {formatBrandLastLogin(user.lastLoginAt)}
            </div>
          </div>
        ))}
      </div>

      <div
        className="bm-unavailable"
        style={{ marginTop: 20 }}
        data-testid="portfolio-access-legend"
      >
        <h2 style={{ fontSize: 15, margin: '0 0 8px' }}>Access levels (WHERE)</h2>
        <p style={{ margin: '0 0 8px', fontSize: 13, color: '#666' }}>
          Full / Limited Brand access is separate from Client Role. Brands
          column shows UserBrandAccess for this Master Account — never inferred
          from Client role.
        </p>
        <p style={{ margin: 0, fontSize: 12, color: '#888' }}>
          Full = corporate WHERE · Limited = franchisee WHERE (location
          entitlements). Role = WHAT the user can do.
        </p>
      </div>
    </>
  );

  const renderUsersTab = () =>
    isBrandScoped ? renderBrandScopedUsersTab() : renderPortfolioUsersTab();
  
  // Render the Roles tab content — canonical enums only; no Create Role invent.
  const renderRolesTab = () => (
    <>
      <div className="profile-header" style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '30px',
        position: 'relative',
        width: '100%'
      }}>
        <h2>{isBrandScoped ? 'Brand Roles' : 'Client Roles'}</h2>
      </div>

      <p style={{ marginTop: 0, color: '#666', fontSize: 13 }} data-testid="roles-doctrine-note">
        {isBrandScoped
          ? 'Brand Role (WHAT) is separate from Access Full/Limited (WHERE). Counts come from the live Brand user roster.'
          : 'Canonical Client roles from UserMembership. Counts come from the live portfolio roster. Editor and Uploader are not platform roles.'}
      </p>
      
      <div
        className="roles-table"
        style={{ marginTop: '20px', backgroundColor: 'white', borderRadius: '8px' }}
        data-testid="canonical-roles-table"
      >
        <div className="table-header" style={{ display: 'grid', gridTemplateColumns: '1.2fr 3fr 1.4fr 0.8fr', padding: '12px 16px', backgroundColor: '#1C2632', color: 'white', borderTopLeftRadius: '8px', borderTopRightRadius: '8px' }}>
          <div className="col-name" style={{ color: 'white' }}>Role</div>
          <div className="col-desc" style={{ color: 'white' }}>Description</div>
          <div className="col-permissions" style={{ color: 'white' }}>Axis</div>
          <div className="col-users" style={{ color: 'white' }}>Users</div>
        </div>
        
        {roles.map((role) => (
          <div
            className="table-row"
            key={role.id}
            data-testid="canonical-role-row"
            data-role-name={role.name}
            style={{ display: 'grid', gridTemplateColumns: '1.2fr 3fr 1.4fr 0.8fr', padding: '12px 16px', borderBottom: '1px solid #e5e5e5', alignItems: 'center' }}
          >
            <div className="col-name">{role.label || role.name}</div>
            <div className="col-desc">{role.description}</div>
            <div className="col-permissions">{role.permissions}</div>
            <div className="col-users">{role.users}</div>
          </div>
        ))}
      </div>
    </>
  );

  // Add this useEffect to the UserManagementPage component to respond to URL changes
  useEffect(() => {
    // Update active tab when URL changes
    if (location.pathname.includes('/roles')) {
      setActiveTab('roles');
    } else if (location.pathname.includes('/users') || location.pathname.includes('/user-management')) {
      setActiveTab('users');
    }
  }, [location.pathname]);

  // This is our main rendering which reuses the simplified structure
  return (
    <div data-testid="user-management-page" data-um-scope={isBrandScoped ? "brand" : isPortfolioScoped ? "portfolio" : "unknown"}>
      <h1>User Management</h1>
      {isPortfolioScoped ? (
        <p className="subtitle" data-testid="user-management-portfolio-scope">
          Client / portfolio user administration
        </p>
      ) : null}
      
      <div className="user-management-content" style={{ width: '100%' }}>
        <div className="content-area" style={{ width: '100%' }}>
          {activeTab === 'users' ? renderUsersTab() : renderRolesTab()}
        </div>
      </div>
      
      {/* Add User Modal */}
      {showNewUserModal && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div className="modal-content" style={{ backgroundColor: 'white', borderRadius: '8px', width: '100%', maxWidth: '500px', padding: '24px' }}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3>Add User</h3>
              <button className="close-btn" onClick={() => setShowNewUserModal(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px' }}>Name *</label>
                <input
                  type="text"
                  value={newUser.name}
                  onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                  placeholder="Enter user name"
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>
              
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px' }}>Email *</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  placeholder="Enter user email"
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>
              
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px' }}>Role *</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  <option value="">Select a role</option>
                  {roles.map(role => (
                    <option key={role.id} value={role.name}>{role.name}</option>
                  ))}
                </select>
              </div>
              
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px' }}>Authentication</label>
                <select
                  value={newUser.authentication}
                  onChange={(e) => setNewUser({...newUser, authentication: e.target.value})}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  <option value="Two-step">Two-step</option>
                  <option value="Password">Password only</option>
                </select>
              </div>
            </div>
            
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', marginTop: '24px' }}>
              <button 
                className="cancel-btn" 
                onClick={() => setShowNewUserModal(false)}
                style={{ padding: '8px 16px', border: '1px solid #ddd', borderRadius: '4px', background: 'none' }}
              >
                Cancel
              </button>
              <button 
                className="save-btn" 
                onClick={handleCreateUser}
                style={{ padding: '8px 16px', backgroundColor: '#E32726', color: 'white', border: 'none', borderRadius: '4px' }}
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Edit User Modal */}
      {showEditUserModal && selectedUser && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div className="modal-content" style={{ backgroundColor: 'white', borderRadius: '8px', width: '100%', maxWidth: '500px', padding: '24px' }}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3>Edit User</h3>
              <button className="close-btn" onClick={() => setShowEditUserModal(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px' }}>Name *</label>
                <input
                  type="text"
                  value={selectedUser.name}
                  onChange={(e) => setSelectedUser({...selectedUser, name: e.target.value})}
                  placeholder="Enter user name"
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>
              
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px' }}>Email *</label>
                <input
                  type="email"
                  value={selectedUser.email}
                  onChange={(e) => setSelectedUser({...selectedUser, email: e.target.value})}
                  placeholder="Enter user email"
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>
              
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px' }}>Role *</label>
                <select
                  value={selectedUser.role}
                  onChange={(e) => setSelectedUser({...selectedUser, role: e.target.value})}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  <option value="">Select a role</option>
                  {roles.map(role => (
                    <option key={role.id} value={role.name}>{role.name}</option>
                  ))}
                </select>
              </div>
              
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px' }}>Authentication</label>
                <select
                  value={selectedUser.authentication}
                  onChange={(e) => setSelectedUser({...selectedUser, authentication: e.target.value})}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  <option value="Two-step">Two-step</option>
                  <option value="Password">Password only</option>
                </select>
              </div>
            </div>
            
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', marginTop: '24px' }}>
              <button 
                className="cancel-btn" 
                onClick={() => setShowEditUserModal(false)}
                style={{ padding: '8px 16px', border: '1px solid #ddd', borderRadius: '4px', background: 'none' }}
              >
                Cancel
              </button>
              <button 
                className="save-btn" 
                onClick={handleSaveUserEdit}
                style={{ padding: '8px 16px', backgroundColor: '#E32726', color: 'white', border: 'none', borderRadius: '4px' }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete User Confirmation Modal */}
      {showDeleteUserModal && selectedUser && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div className="modal-content" style={{ backgroundColor: 'white', borderRadius: '8px', width: '100%', maxWidth: '500px', padding: '24px' }}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3>Delete User</h3>
              <button className="close-btn" onClick={() => setShowDeleteUserModal(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>×</button>
            </div>
            
            <div className="modal-body">
              <p>Are you sure you want to delete user <strong>{selectedUser.name}</strong>?</p>
              <p>This action cannot be undone.</p>
            </div>
            
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', marginTop: '24px' }}>
              <button 
                className="cancel-btn" 
                onClick={() => setShowDeleteUserModal(false)}
                style={{ padding: '8px 16px', border: '1px solid #ddd', borderRadius: '4px', background: 'none' }}
              >
                Cancel
              </button>
              <button 
                className="delete-btn" 
                onClick={handleConfirmDeleteUser}
                style={{ padding: '8px 16px', backgroundColor: '#E32726', color: 'white', border: 'none', borderRadius: '4px' }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* New Role Modal */}
      {showNewRoleModal && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div className="modal-content" style={{ backgroundColor: 'white', borderRadius: '8px', width: '100%', maxWidth: '500px', padding: '24px' }}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3>Create Role</h3>
              <button className="close-btn" onClick={() => setShowNewRoleModal(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px' }}>Name *</label>
                <input
                  type="text"
                  value={newRole.name}
                  onChange={(e) => setNewRole({...newRole, name: e.target.value})}
                  placeholder="Enter role name"
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>
              
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px' }}>Description *</label>
                <input
                  type="text"
                  value={newRole.description}
                  onChange={(e) => setNewRole({...newRole, description: e.target.value})}
                  placeholder="Enter role description"
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>
              
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px' }}>Permissions *</label>
                <select
                  value={newRole.permissions}
                  onChange={(e) => setNewRole({...newRole, permissions: e.target.value})}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  <option value="None">None</option>
                  <option value="View Only">View Only</option>
                  <option value="Limited">Limited</option>
                  <option value="All">All</option>
                </select>
              </div>
            </div>
            
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', marginTop: '24px' }}>
              <button 
                className="cancel-btn" 
                onClick={() => setShowNewRoleModal(false)}
                style={{ padding: '8px 16px', border: '1px solid #ddd', borderRadius: '4px', background: 'none' }}
              >
                Cancel
              </button>
              <button 
                className="create-btn" 
                onClick={handleCreateRole}
                style={{ padding: '8px 16px', backgroundColor: '#E32726', color: 'white', border: 'none', borderRadius: '4px' }}
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Edit Role Modal */}
      {showEditRoleModal && selectedRole && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div className="modal-content" style={{ backgroundColor: 'white', borderRadius: '8px', width: '100%', maxWidth: '500px', padding: '24px' }}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3>Edit Role</h3>
              <button className="close-btn" onClick={() => setShowEditRoleModal(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px' }}>Name *</label>
                <input
                  type="text"
                  value={selectedRole.name}
                  onChange={(e) => setSelectedRole({...selectedRole, name: e.target.value})}
                  placeholder="Enter role name"
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>
              
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px' }}>Description *</label>
                <input
                  type="text"
                  value={selectedRole.description}
                  onChange={(e) => setSelectedRole({...selectedRole, description: e.target.value})}
                  placeholder="Enter role description"
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>
              
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px' }}>Permissions *</label>
                <select
                  value={selectedRole.permissions}
                  onChange={(e) => setSelectedRole({...selectedRole, permissions: e.target.value})}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  <option value="None">None</option>
                  <option value="View Only">View Only</option>
                  <option value="Limited">Limited</option>
                  <option value="All">All</option>
                </select>
              </div>
            </div>
            
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', marginTop: '24px' }}>
              <button 
                className="cancel-btn" 
                onClick={() => setShowEditRoleModal(false)}
                style={{ padding: '8px 16px', border: '1px solid #ddd', borderRadius: '4px', background: 'none' }}
              >
                Cancel
              </button>
              <button 
                className="save-btn" 
                onClick={handleSaveRoleEdit}
                style={{ padding: '8px 16px', backgroundColor: '#E32726', color: 'white', border: 'none', borderRadius: '4px' }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Role Confirmation Modal */}
      {showDeleteRoleModal && selectedRole && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div className="modal-content" style={{ backgroundColor: 'white', borderRadius: '8px', width: '100%', maxWidth: '500px', padding: '24px' }}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3>Delete Role</h3>
              <button className="close-btn" onClick={() => setShowDeleteRoleModal(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>×</button>
            </div>
            
            <div className="modal-body">
              <p>Are you sure you want to delete role <strong>{selectedRole.name}</strong>?</p>
              <p>This action cannot be undone.</p>
              {selectedRole.users > 0 && (
                <p style={{ color: '#E32726' }}>
                  Warning: This role is currently assigned to {selectedRole.users} user(s).
                </p>
              )}
            </div>
            
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', marginTop: '24px' }}>
              <button 
                className="cancel-btn" 
                onClick={() => setShowDeleteRoleModal(false)}
                style={{ padding: '8px 16px', border: '1px solid #ddd', borderRadius: '4px', background: 'none' }}
              >
                Cancel
              </button>
              <button 
                className="delete-btn" 
                onClick={handleConfirmDeleteRole}
                style={{ padding: '8px 16px', backgroundColor: '#E32726', color: 'white', border: 'none', borderRadius: '4px' }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Assign Role to Users Modal */}
      {showAssignRoleModal && selectedRole && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div className="modal-content" style={{ 
            backgroundColor: 'white', 
            borderRadius: '8px', 
            width: '100%', 
            maxWidth: '500px',
            minHeight: '500px',
            maxHeight: '90vh',
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto'
          }}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3>Assign {selectedRole.name} role to users</h3>
              <button className="close-btn" onClick={() => setShowAssignRoleModal(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>×</button>
            </div>
            
            <div className="modal-body" style={{ flex: '1 1 auto' }}>
              <p>Select users you want to assign this role to.</p>
              <div className="form-group" style={{ marginBottom: '80px' }}>
                <label style={{ display: 'block', marginBottom: '8px' }}>Select Users</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    placeholder="Begin typing to select users..."
                    value={searchTerm}
                    onChange={handleUserSearch}
                    style={{ 
                      width: '100%', 
                      padding: '8px', 
                      border: '1px solid #ddd', 
                      borderRadius: '4px',
                      marginBottom: '16px'
                    }}
                  />
                  
                  {selectedUser && (
                    <div style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: '#f5f5f5',
                      marginTop: '8px',
                      marginBottom: '16px'
                    }}>
                      <div style={{ fontWeight: 'bold' }}>{selectedUser.name}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>{selectedUser.email}</div>
                    </div>
                  )}
                  
                  {showUserDropdown && (
                    <div style={{ 
                      position: 'absolute', 
                      top: 'calc(100% - 8px)',
                      left: 0, 
                      width: '100%', 
                      maxHeight: '200px',
                      overflowY: 'auto', 
                      background: 'white', 
                      border: '1px solid #ddd', 
                      borderRadius: '4px',
                      zIndex: 9999,
                      padding: 0,
                      margin: 0
                    }}>
                      {users.filter(user => 
                        user.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                        user.email.toLowerCase().includes(searchTerm.toLowerCase())
                      ).map((user, index, filteredArray) => (
                        <div
                          key={index}
                          style={{ 
                            padding: '10px 12px',
                            borderBottom: index < filteredArray.length - 1 ? '1px solid #eee' : 'none',
                            cursor: 'pointer',
                            backgroundColor: 'white',
                            minHeight: '40px',
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            boxSizing: 'border-box'
                          }}
                          onClick={() => {
                            setSearchTerm(user.name);
                            setSelectedUser(user);
                            setShowUserDropdown(false);
                          }}
                        >
                          <span style={{ 
                            fontWeight: 'bold', 
                            fontSize: '14px',
                            display: 'block',
                            textOverflow: 'unset',
                            whiteSpace: 'normal',
                            overflow: 'visible',
                            wordBreak: 'break-word'
                          }}>{user.name}</span>
                          <span style={{ 
                            fontSize: '12px', 
                            color: '#666', 
                            marginTop: '4px',
                            display: 'block',
                            textOverflow: 'unset',
                            whiteSpace: 'normal',
                            overflow: 'visible',
                            wordBreak: 'break-word'
                          }}>{user.email}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="modal-footer" style={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              gap: '16px', 
              marginTop: '24px',
              paddingTop: '24px',
              borderTop: '1px solid #eee'
            }}>
              <button 
                className="cancel-btn" 
                onClick={() => setShowAssignRoleModal(false)}
                style={{ padding: '8px 16px', border: '1px solid #ddd', borderRadius: '4px', background: 'none' }}
              >
                Cancel
              </button>
              <button 
                className="assign-btn" 
                onClick={handleAssignRole}
                disabled={!selectedUser}
                style={{ 
                  padding: '8px 16px', 
                  backgroundColor: '#E32726', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '4px',
                  opacity: selectedUser ? 1 : 0.5,
                  cursor: selectedUser ? 'pointer' : 'not-allowed'
                }}
              >
                Assign
              </button>
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
};

export default UserManagementPage; 